/**
 * The deterministic skeleton beneath Rosalie's stealth encounters.
 *
 * This file deliberately knows nothing about scene graphs, Rapier, meshes, or
 * wall colliders. A zone supplies its static light blockers while baking, and
 * the game supplies the current ray-cast result while simulating. Keeping that
 * boundary hard is what lets encounter logic be replayed in Node exactly as it
 * ran during a fixed simulation step.
 */

import { emissionRadius, type GaitKind, type SurfaceKind } from '@/audio/sounds/foley';

/** A world-space position, deliberately independent of Three and Rapier. */
export interface Position {
  x: number;
  y: number;
  z: number;
}

/** Axis-aligned solid used both for authored cover and baked light blockers. */
export interface Aabb {
  min: Position;
  max: Position;
}

export interface CoverVolume extends Aabb {
  /** 0 is cosmetic cover; 1 makes this volume fully concealing. */
  concealment: number;
}

export interface PointLightEmitter {
  kind: 'point';
  position: Position;
  radius: number;
  intensity: number;
}

export interface BattenLightEmitter {
  kind: 'batten';
  start: Position;
  end: Position;
  /** The half-width of the tube's elliptical pool of light. */
  radius: number;
  intensity: number;
}

export type LightEmitter = PointLightEmitter | BattenLightEmitter;

export interface LightGridBounds {
  minX: number;
  minZ: number;
  width: number;
  depth: number;
  /** Height at which the floor exposure is baked. Defaults to floor level. */
  sampleY?: number;
}

/**
 * A baked zone-light field. `values` includes the outer edge samples so that
 * bilinear interpolation reaches every point inside `bounds`, rather than
 * leaving a half-cell dark border around a hall.
 */
export interface LightGrid {
  bounds: LightGridBounds;
  cellSize: number;
  columns: number;
  rows: number;
  values: Float32Array;
  sample(x: number, z: number): number;
}

export interface ExposureBreakdown {
  lightLevel: number;
  concealment: number;
  exposure: number;
}

export interface Watcher {
  id: string;
  position: Position;
  /** Radians: 0 faces +Z, increasing clockwise toward +X. */
  facing: number;
  coneHalfAngle: number;
  range: number;
  eyeHeight: number;
}

export interface VisibilityContributions {
  angle: number;
  distance: number;
  exposure: number;
  lineOfSight: number;
}

export interface VisibilityEvaluation {
  visibility: number;
  contributions: VisibilityContributions;
  exposure: ExposureBreakdown;
}

export interface SoundEvent {
  position: Position;
  radius: number;
}

export interface SoundContribution extends SoundEvent {
  suspicionAdded: number;
}

export type SuspicionState = 'unaware' | 'suspicious' | 'detected';

/**
 * The data a level designer and playtester need to reconstruct a detection.
 * Keeping zeroes as numbers rather than omitting fields makes this useful to
 * tools as well as people: `visibility` is always the product of the four
 * contributions, and `exposure.exposure` is always light times remaining cover.
 */
export interface DetectionReason {
  watcherId: string;
  visibility: number;
  contributions: VisibilityContributions;
  exposure: ExposureBreakdown;
  mostRecentSound: SoundContribution | null;
}

export interface WatcherSuspicion {
  watcherId: string;
  suspicion: number;
  state: SuspicionState;
  /** Seconds of uninterrupted zero vision; exposed so a diegetic reaction can match it. */
  zeroVisibilitySeconds: number;
  lastVisibility: VisibilityEvaluation;
  lastDetection: DetectionReason | null;
  /** The most recent sound this watcher was within earshot of, or null. */
  lastSound: SoundContribution | null;
  /** How many sounds this watcher has heard since construction. Monotonic. */
  soundsHeard: number;
  /**
   * Where this watcher IS, and it is here so that a gate can read it back.
   *
   * A moving watcher's position is written by `moveWatcher` from outside, which
   * means the only way to check what was written is to ask the system — and
   * until this field existed there was no way to ask. Beat 10's debug seam
   * duly reported `hedgerowGroundY(dog.x, dog.z)`, recomputed with the same
   * expression `main.ts` passes in, so its "the eye is at a dog's height" gate
   * agreed with itself no matter what the caller had actually stored. That is
   * D-100 exactly, and it is the SECOND time this beat has written it: stage 2
   * shipped a watcher authored at `ground + eyeHeight`, an eye at 1.10 m, and
   * no level assertion could see it either.
   *
   * **`position.y` is the base and `eyeHeight` is added to it** by
   * `evaluateVisibility`. A caller that adds the eye height itself gets a dog
   * looking down into the channel D-115 built to hide her, and this is the
   * field that says so.
   */
  position: Position;
}

export interface StealthSnapshot {
  alarmLevel: number;
  watchers: readonly WatcherSuspicion[];
  lastDetection: DetectionReason | null;
}

/** The world-facing ray query. `true` means the target can be seen. */
export type HasLineOfSight = (from: Position, to: Position) => boolean;

/**
 * Every authored feel number is kept here, rather than hidden in equations.
 * The comments describe player experience because those are the outcomes a
 * designer can tune; the implementation details belong beside their equations.
 */
export const STEALTH_TUNING = {
  /**
   * Half-metre samples make pools of light feel continuous around Rosalie while
   * keeping a large hall cheap to load. A 40 by 20 m hall has 81 by 41 = 3,321
   * samples; each emitter casts at most that many tiny AABB tests once at load.
   */
  lightCellSize: 0.5,
  /** A sighted Rosalie reaches an obvious reaction in a little over a second. */
  suspicionRisePerSecond: 42,
  /** A worker gives the player a two-second chance to break sight after a glance. */
  suspicionGraceSeconds: 2,
  /** Lost suspicion drains slower than it rises, so a near-miss still has weight. */
  suspicionDecayPerSecond: 12,
  /** A loud nearby footfall makes a worker meaningfully alert but is not instant capture. */
  soundSuspicionAtSource: 22,
  /**
   * **Hearing alerts; only sight detects.** Sound raises suspicion to this
   * ceiling and no further; the last step to 100 is always a tick on which a
   * watcher can see her, so every detection has a visibility in its record
   * (spec §5.1's fairness rule). This was true by accident of order until
   * session 76 — `step` decayed before it checked, so footfalls sat a watcher
   * at 99.8 — and false inside the 2 s grace after a sighting, where no decay
   * runs and a footfall could capture her unseen (playthrough audit row 25).
   * `stealth.spec.ts` holds both halves.
   */
  soundSuspicionCeiling: 99,
  /** One alarm pip takes three quiet minutes to fade, preserving consequences without permanence. */
  alarmDecayPerSecond: 1 / 180,
  /** The eye-to-snout ray gives low cover a meaningful chance to hide Rosalie. */
  targetEyeHeight: 0.2,
  /** A partially hidden target is still suspicious once the meter reaches this threshold. */
  suspiciousThreshold: 35,
} as const;

type MutableWatcherState = {
  watcher: Watcher;
  suspicion: number;
  zeroVisibilitySeconds: number;
  wasDetected: boolean;
  lastVisibility: VisibilityEvaluation;
  lastSound: SoundContribution | null;
  soundsHeard: number;
  lastDetection: DetectionReason | null;
};

const EMPTY_VISIBILITY: VisibilityEvaluation = {
  visibility: 0,
  contributions: { angle: 0, distance: 0, exposure: 0, lineOfSight: 0 },
  exposure: { lightLevel: 0, concealment: 0, exposure: 0 },
};

/** Clamp authored content and floating-point edges to the promises in the API. */
function clamp(value: number, min = 0, max = 1): number {
  return Math.min(max, Math.max(min, value));
}

function smoothFalloff(normalisedDistance: number): number {
  const remaining = clamp(1 - normalisedDistance);
  return remaining * remaining;
}

function rayHitsAabb(from: Position, to: Position, box: Aabb): boolean {
  let enter = 0;
  let exit = 1;
  const start: readonly number[] = [from.x, from.y, from.z];
  const end: readonly number[] = [to.x, to.y, to.z];
  const minimum: readonly number[] = [box.min.x, box.min.y, box.min.z];
  const maximum: readonly number[] = [box.max.x, box.max.y, box.max.z];

  for (let axis = 0; axis < 3; axis += 1) {
    const origin = start[axis];
    const destination = end[axis];
    const min = minimum[axis];
    const max = maximum[axis];
    if (origin === undefined || destination === undefined || min === undefined || max === undefined) {
      return false;
    }
    const delta = destination - origin;
    if (Math.abs(delta) < 0.000001) {
      if (origin < min || origin > max) return false;
      continue;
    }
    const inverse = 1 / delta;
    let near = (min - origin) * inverse;
    let far = (max - origin) * inverse;
    if (near > far) [near, far] = [far, near];
    enter = Math.max(enter, near);
    exit = Math.min(exit, far);
    if (enter > exit) return false;
  }

  // A blocker touching a source or destination does not create a one-cell black
  // seam. The source must be on the other side of actual opaque volume instead.
  return exit > 0.000001 && enter < 0.999999;
}

function blockedByOccluder(from: Position, to: Position, occluders: readonly Aabb[]): boolean {
  return occluders.some((occluder) => rayHitsAabb(from, to, occluder));
}

function pointLightAt(emitter: PointLightEmitter, cell: Position, occluders: readonly Aabb[]): number {
  const dx = cell.x - emitter.position.x;
  const dy = cell.y - emitter.position.y;
  const dz = cell.z - emitter.position.z;
  const distance = Math.hypot(dx, dy, dz);
  if (distance >= emitter.radius || blockedByOccluder(emitter.position, cell, occluders)) return 0;
  return emitter.intensity * smoothFalloff(distance / emitter.radius);
}

function battenLightAt(emitter: BattenLightEmitter, cell: Position, occluders: readonly Aabb[]): number {
  const axisX = emitter.end.x - emitter.start.x;
  const axisY = emitter.end.y - emitter.start.y;
  const axisZ = emitter.end.z - emitter.start.z;
  const length = Math.hypot(axisX, axisY, axisZ);
  if (length < 0.000001 || emitter.radius <= 0) return 0;

  const midX = (emitter.start.x + emitter.end.x) * 0.5;
  const midY = (emitter.start.y + emitter.end.y) * 0.5;
  const midZ = (emitter.start.z + emitter.end.z) * 0.5;
  const unitX = axisX / length;
  const unitY = axisY / length;
  const unitZ = axisZ / length;
  const relX = cell.x - midX;
  const relY = cell.y - midY;
  const relZ = cell.z - midZ;
  const along = relX * unitX + relY * unitY + relZ * unitZ;
  const perpendicular = Math.sqrt(Math.max(0, relX * relX + relY * relY + relZ * relZ - along * along));

  // A tube's pool is an ellipse: adding its half length to the radius keeps the
  // visible pool beyond each end soft, while avoiding the circular spotlight a
  // point-source approximation would make beneath a 1.5 m fluorescent batten.
  const normalised = Math.hypot(along / (length * 0.5 + emitter.radius), perpendicular / emitter.radius);
  if (normalised >= 1) return 0;

  const unclamped = clamp((along + length * 0.5) / length);
  const source = {
    x: emitter.start.x + axisX * unclamped,
    y: emitter.start.y + axisY * unclamped,
    z: emitter.start.z + axisZ * unclamped,
  };
  if (blockedByOccluder(source, cell, occluders)) return 0;
  return emitter.intensity * smoothFalloff(normalised);
}

/** Bake a light grid once at zone load; sampling it during play is O(1). */
export function bakeLightGrid(
  bounds: LightGridBounds,
  emitters: readonly LightEmitter[],
  occluders: readonly Aabb[] = [],
  cellSize = STEALTH_TUNING.lightCellSize,
): LightGrid {
  if (bounds.width <= 0 || bounds.depth <= 0 || cellSize <= 0) {
    throw new Error('light-grid bounds and cell size must be positive');
  }
  const columns = Math.ceil(bounds.width / cellSize) + 1;
  const rows = Math.ceil(bounds.depth / cellSize) + 1;
  const values = new Float32Array(columns * rows);
  const sampleY = bounds.sampleY ?? 0;

  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      // NOT clamped to the bounds. The last column and row are allowed to sit
      // slightly outside the room, so every cell is exactly `cellSize` apart.
      //
      // Clamping them was the obvious thing and it was wrong twice over, because
      // `sample()` computes its bilinear weights from `cellSize` and therefore
      // assumes uniform spacing: near a shortened final cell the weights were
      // simply incorrect, and the bounds check let a caller sample almost a full
      // cell PAST the room and get the boundary brightness instead of nothing —
      // so standing in a doorway outside a lit room scored as still exposed.
      // Uniform spacing costs one row of samples and removes the special case.
      // Found on independent review.
      const cell: Position = {
        x: bounds.minX + column * cellSize,
        y: sampleY,
        z: bounds.minZ + row * cellSize,
      };
      let brightness = 0;
      for (const emitter of emitters) {
        brightness += emitter.kind === 'point'
          ? pointLightAt(emitter, cell, occluders)
          : battenLightAt(emitter, cell, occluders);
      }
      values[row * columns + column] = brightness;
    }
  }

  const sample = (x: number, z: number): number => {
    // Rejected against the DECLARED bounds, not against the grid, which extends
    // past them by up to one cell. Outside the room there is no light, and a
    // caller that asks is asking about somewhere this grid does not describe.
    if (
      x < bounds.minX ||
      z < bounds.minZ ||
      x > bounds.minX + bounds.width ||
      z > bounds.minZ + bounds.depth
    ) {
      return 0;
    }
    const localX = (x - bounds.minX) / cellSize;
    const localZ = (z - bounds.minZ) / cellSize;
    const x0 = Math.floor(localX);
    const z0 = Math.floor(localZ);
    const x1 = Math.min(columns - 1, x0 + 1);
    const z1 = Math.min(rows - 1, z0 + 1);
    const tx = localX - x0;
    const tz = localZ - z0;
    const topLeft = values[z0 * columns + x0] ?? 0;
    const topRight = values[z0 * columns + x1] ?? 0;
    const bottomLeft = values[z1 * columns + x0] ?? 0;
    const bottomRight = values[z1 * columns + x1] ?? 0;
    const top = topLeft + (topRight - topLeft) * tx;
    const bottom = bottomLeft + (bottomRight - bottomLeft) * tx;
    return top + (bottom - top) * tz;
  };

  return { bounds, cellSize, columns, rows, values, sample };
}

/** The strongest overlapping volume wins; two hiding places must not add to invisibility. */
export function bestConcealment(position: Position, cover: readonly CoverVolume[]): number {
  let best = 0;
  for (const volume of cover) {
    if (
      position.x >= volume.min.x && position.x <= volume.max.x &&
      position.y >= volume.min.y && position.y <= volume.max.y &&
      position.z >= volume.min.z && position.z <= volume.max.z
    ) {
      best = Math.max(best, clamp(volume.concealment));
    }
  }
  return best;
}

export function exposureAt(position: Position, lightGrid: LightGrid, cover: readonly CoverVolume[]): ExposureBreakdown {
  const lightLevel = lightGrid.sample(position.x, position.z);
  const concealment = bestConcealment(position, cover);
  return { lightLevel, concealment, exposure: lightLevel * (1 - concealment) };
}

function angleDifference(a: number, b: number): number {
  return Math.abs(Math.atan2(Math.sin(a - b), Math.cos(a - b)));
}

/** Evaluate sight without changing suspicion, useful to author and test encounters. */
export function evaluateVisibility(
  watcher: Watcher,
  target: Position,
  lightGrid: LightGrid,
  cover: readonly CoverVolume[],
  hasLineOfSight: HasLineOfSight,
): VisibilityEvaluation {
  const dx = target.x - watcher.position.x;
  const dz = target.z - watcher.position.z;
  const horizontalDistance = Math.hypot(dx, dz);
  const targetBearing = Math.atan2(dx, dz);
  const angularDistance = angleDifference(targetBearing, watcher.facing);
  const angle = watcher.coneHalfAngle <= 0 ? 0 : smoothFalloff(angularDistance / watcher.coneHalfAngle);
  const distance = watcher.range <= 0 ? 0 : smoothFalloff(horizontalDistance / watcher.range);
  const exposure = exposureAt(target, lightGrid, cover);
  const from = { x: watcher.position.x, y: watcher.position.y + watcher.eyeHeight, z: watcher.position.z };
  const to = { x: target.x, y: target.y + STEALTH_TUNING.targetEyeHeight, z: target.z };
  const lineOfSight = hasLineOfSight(from, to) ? 1 : 0;
  const contributions = { angle, distance, exposure: exposure.exposure, lineOfSight };
  return {
    visibility: contributions.angle * contributions.distance * contributions.exposure * contributions.lineOfSight,
    contributions,
    exposure,
  };
}

function suspicionState(value: number): SuspicionState {
  if (value >= 100) return 'detected';
  if (value >= STEALTH_TUNING.suspiciousThreshold) return 'suspicious';
  return 'unaware';
}

/**
 * Zone-local deterministic state. Call `step` exactly once per fixed simulation
 * step; no wall-clock time, random value, or presentation state enters here.
 */
export class StealthSystem {
  readonly #lightGrid: LightGrid;
  readonly #cover: readonly CoverVolume[];
  readonly #hasLineOfSight: HasLineOfSight;
  readonly #watchers = new Map<string, MutableWatcherState>();
  #alarmLevel = 0;
  #lastDetection: DetectionReason | null = null;

  constructor(
    lightGrid: LightGrid,
    cover: readonly CoverVolume[],
    watchers: readonly Watcher[],
    hasLineOfSight: HasLineOfSight,
  ) {
    this.#lightGrid = lightGrid;
    this.#cover = cover;
    this.#hasLineOfSight = hasLineOfSight;
    for (const watcher of watchers) {
      if (this.#watchers.has(watcher.id)) throw new Error(`duplicate stealth watcher: ${watcher.id}`);
      this.#watchers.set(watcher.id, {
        watcher,
        suspicion: 0,
        zeroVisibilitySeconds: 0,
        wasDetected: false,
        lastVisibility: EMPTY_VISIBILITY,
        lastSound: null,
        soundsHeard: 0,
        lastDetection: null,
      });
    }
  }

  /**
   * Emit a gait sound using the audio system's single source of truth for range.
   * The sound graph and stealth may now tune together instead of maintaining a
   * second, silently diverging radius table.
   */
  emitFootfall(position: Position, surface: SurfaceKind, gait: GaitKind): void {
    this.emitSound({ position, radius: emissionRadius(surface, gait) });
  }

  /** Sound travels around corners: only radial distance matters to hearing. */
  emitSound(event: SoundEvent): void {
    if (event.radius <= 0) return;
    for (const state of this.#watchers.values()) {
      const distance = Math.hypot(
        state.watcher.position.x - event.position.x,
        state.watcher.position.y - event.position.y,
        state.watcher.position.z - event.position.z,
      );
      if (distance >= event.radius) continue;
      const suspicionAdded = STEALTH_TUNING.soundSuspicionAtSource * (1 - distance / event.radius);
      // Never lowers what sight already raised above the ceiling.
      const ceiling = Math.max(state.suspicion, STEALTH_TUNING.soundSuspicionCeiling);
      state.suspicion = clamp(state.suspicion + suspicionAdded, 0, ceiling);
      state.lastSound = { position: { ...event.position }, radius: event.radius, suspicionAdded };
      state.soundsHeard += 1;
    }
  }

  /** Move a live watcher without rebuilding the encounter and losing its history. */
  moveWatcher(id: string, position: Position, facing: number): void {
    const state = this.#watchers.get(id);
    if (!state) throw new Error(`unknown stealth watcher: ${id}`);
    if (![position.x, position.y, position.z, facing].every(Number.isFinite)) {
      throw new Error('stealth watcher position and facing must be finite');
    }
    state.watcher.position.x = position.x;
    state.watcher.position.y = position.y;
    state.watcher.position.z = position.z;
    state.watcher.facing = facing;
  }

  /** Clear the immediate reaction without erasing the alarm or its instrumentation. */
  resetSuspicion(id: string): void {
    const state = this.#watchers.get(id);
    if (!state) throw new Error(`unknown stealth watcher: ${id}`);
    state.suspicion = 0;
    state.zeroVisibilitySeconds = 0;
    state.wasDetected = false;
  }

  step(dt: number, target: Position): StealthSnapshot {
    if (dt < 0 || !Number.isFinite(dt)) throw new Error('stealth step must use a finite non-negative dt');
    this.#alarmLevel = clamp(this.#alarmLevel - STEALTH_TUNING.alarmDecayPerSecond * dt, 0, 3);

    for (const state of this.#watchers.values()) {
      const visibility = evaluateVisibility(
        state.watcher,
        target,
        this.#lightGrid,
        this.#cover,
        this.#hasLineOfSight,
      );
      state.lastVisibility = visibility;
      if (visibility.visibility > 0) {
        state.zeroVisibilitySeconds = 0;
        state.suspicion = clamp(
          state.suspicion + visibility.visibility * STEALTH_TUNING.suspicionRisePerSecond * dt,
          0,
          100,
        );
      } else {
        const graceLeft = Math.max(0, STEALTH_TUNING.suspicionGraceSeconds - state.zeroVisibilitySeconds);
        const decaySeconds = Math.max(0, dt - graceLeft);
        state.zeroVisibilitySeconds += dt;
        state.suspicion = clamp(state.suspicion - STEALTH_TUNING.suspicionDecayPerSecond * decaySeconds, 0, 100);
      }

      const detected = state.suspicion >= 100;
      if (detected && !state.wasDetected) {
        const reason: DetectionReason = {
          watcherId: state.watcher.id,
          visibility: visibility.visibility,
          contributions: { ...visibility.contributions },
          exposure: { ...visibility.exposure },
          mostRecentSound: state.lastSound === null ? null : {
            position: { ...state.lastSound.position },
            radius: state.lastSound.radius,
            suspicionAdded: state.lastSound.suspicionAdded,
          },
        };
        state.lastDetection = reason;
        this.#lastDetection = reason;
        this.#alarmLevel = clamp(this.#alarmLevel + 1, 0, 3);
      }
      state.wasDetected = detected;
    }
    return this.snapshot();
  }

  snapshot(): StealthSnapshot {
    const watchers = [...this.#watchers.values()].map((state) => ({
      watcherId: state.watcher.id,
      suspicion: state.suspicion,
      state: suspicionState(state.suspicion),
      zeroVisibilitySeconds: state.zeroVisibilitySeconds,
      lastVisibility: state.lastVisibility,
      lastDetection: state.lastDetection,
      // A copy, like `lastSound` below: a snapshot that handed out the live
      // `Watcher` would let a reader move a watcher by writing to a debug value.
      position: { ...state.watcher.position },
      lastSound: state.lastSound === null ? null : {
        position: { ...state.lastSound.position },
        radius: state.lastSound.radius,
        suspicionAdded: state.lastSound.suspicionAdded,
      },
      soundsHeard: state.soundsHeard,
    }));
    return { alarmLevel: this.#alarmLevel, watchers, lastDetection: this.#lastDetection };
  }
}
